<template>
  <div>
    <page-title-bar :customTitle="customTitle"></page-title-bar>
    <v-container fluid grid-list-xl pt-0>
      <v-layout row wrap justify-center>
        <v-flex xs12>
          <v-toolbar :elevation="0" height="auto">
            <v-layout row justify-start align-center style="padding: 32px">
              <v-flex xs12 md3>
                <v-select v-model="chosen_year" :items="yearOptions" label="Chọn năm" :disabled="loader"
                  prepend-icon="event" hide-details></v-select>
              </v-flex>

              <v-flex xs12 md9 class="text-right">
                 <v-btn color="success" @click="exportExcel" :loading="exporting" :disabled="loader || !months.length">
                    <v-icon left small>cloud_download</v-icon>
                    Xuất Excel
                 </v-btn>
              </v-flex>
            </v-layout>
          </v-toolbar>

          <div class="mt-3">
            <v-card>
              <v-card-title class="headline">
                Báo cáo kết quả kinh doanh năm {{ chosen_year }}
              </v-card-title>
              <v-divider></v-divider>

              <!-- Loading State -->
              <v-card-text v-if="loader" class="text-center pa-5">
                <v-progress-circular indeterminate color="primary" size="50"></v-progress-circular>
                <p class="mt-3">Đang tải báo cáo...</p>
              </v-card-text>

              <!-- Report Content -->
              <v-card-text v-else-if="months && months.length > 0">
                <div class="table-responsive">
                  <table class="yearly-report-table">
                    <thead>
                      <tr>
                        <th class="text-left sticky-col" style="min-width: 250px">Chỉ tiêu</th>
                        <th v-for="month in months" :key="month.month" class="text-right" style="min-width: 120px">
                          {{ formatMonthDisplay(month.month) }}
                        </th>
                        <th class="text-right sticky-col" style="min-width: 140px">Số dư cộng dồn</th>
                      </tr>
                    </thead>
                    <tbody>
                      <!-- DOANH THU -->
                      <tr class="section-header">
                        <td class="sticky-col"><strong>DOANH THU</strong></td>
                        <td v-for="month in months" :key="`doanhthu_${month.month}`" class="text-right">
                          <strong>{{ month.report_data.tong_doanh_thu | formatCurrency }}</strong>
                        </td>
                        <td class="text-right total-col"><strong>{{ yearly_totals.tong_doanh_thu | formatCurrency }}</strong></td>
                      </tr>
                      
                      <!-- 1. Doanh thu tiền phòng (Grouped) -->
                      <tr class="expandable-row" @click="toggleGeneric('showRoomRevenueDetail')">
                        <td class="sticky-col pl-3">
                          Doanh thu tiền phòng
                          <v-icon small class="ml-1">{{ showRoomRevenueDetail ? 'expand_less' : 'expand_more' }}</v-icon>
                        </td>
                        <td v-for="month in months" :key="`phong_total_${month.month}`" class="text-right">
                          {{ month.report_data.tong_doanh_thu_tien_phong | formatCurrency }}
                        </td>
                        <td class="text-right total-col">{{ yearly_totals.tong_doanh_thu_tien_phong | formatCurrency }}</td>
                      </tr>
                      <template v-if="showRoomRevenueDetail">
                        <!-- 1.1 Doanh thu tiền phòng (Room Price) -->
                        <tr class="expandable-row sub-row" @click="toggleGeneric('showRoomBillDetail')">
                          <td class="sticky-col pl-5">
                            Doanh thu tiền phòng
                            <v-icon small class="ml-1">{{ showRoomBillDetail ? 'expand_less' : 'expand_more' }}</v-icon>
                          </td>
                          <td v-for="month in months" :key="`phong_price_${month.month}`" class="text-right">
                            {{ month.report_data.doanh_thu_tien_phong | formatCurrency }}
                          </td>
                          <td class="text-right total-col">{{ yearly_totals.doanh_thu_tien_phong | formatCurrency }}</td>
                        </tr>
                        <template v-if="showRoomBillDetail">
                          <tr v-for="(item, idx) in revRoomDetailRows" :key="`rev_room_${idx}`" class="sub-row">
                            <td class="sticky-col pl-7" style="padding-left: 56px !important">- {{ item.text }}</td>
                            <td v-for="month in months" :key="`rev_room_${item.key}_${month.month}`" class="text-right">
                              {{ getDetailAmount(month, 'doanh_thu_tien_phong_detail', item.key, 'text') | formatCurrency }}
                            </td>
                            <td class="text-right total-col">{{ getDetailYearlyTotal('doanh_thu_tien_phong_detail', item.key, 'text') | formatCurrency }}</td>
                          </tr>
                        </template>

                        <!-- 1.2 Tiền điện (Expand) -->
                        <tr class="expandable-row sub-row" @click="toggleGeneric('showRevElectric')">
                          <td class="sticky-col pl-5">
                            Doanh thu tiền điện
                            <v-icon small class="ml-1">{{ showRevElectric ? 'expand_less' : 'expand_more' }}</v-icon>
                          </td>
                          <td v-for="month in months" :key="`dien_rev_${month.month}`" class="text-right">
                            {{ month.report_data.doanh_thu_tien_dien | formatCurrency }}
                          </td>
                          <td class="text-right total-col">{{ yearly_totals.doanh_thu_tien_dien | formatCurrency }}</td>
                        </tr>
                        <template v-if="showRevElectric">
                          <tr v-for="(item, idx) in revElectricDetailRows" :key="`rev_elec_${idx}`" class="sub-row">
                            <td class="sticky-col" style="padding-left: 56px">- {{ item.text }}</td>
                            <td v-for="month in months" :key="`rev_elec_${item.key}_${month.month}`" class="text-right">
                              {{ getDetailAmount(month, 'doanh_thu_tien_dien_detail', item.key, 'text') | formatCurrency }}
                            </td>
                            <td class="text-right total-col">{{ getDetailYearlyTotal('doanh_thu_tien_dien_detail', item.key, 'text') | formatCurrency }}</td>
                          </tr>
                        </template>

                        <!-- 1.3 Tiền nước (Expand) -->
                        <tr class="expandable-row sub-row" @click="toggleGeneric('showRevWater')">
                          <td class="sticky-col pl-5">
                            Doanh thu tiền nước
                            <v-icon small class="ml-1">{{ showRevWater ? 'expand_less' : 'expand_more' }}</v-icon>
                          </td>
                          <td v-for="month in months" :key="`nuoc_rev_${month.month}`" class="text-right">
                            {{ month.report_data.doanh_thu_tien_nuoc | formatCurrency }}
                          </td>
                          <td class="text-right total-col">{{ yearly_totals.doanh_thu_tien_nuoc | formatCurrency }}</td>
                        </tr>
                        <template v-if="showRevWater">
                          <tr v-for="(item, idx) in revWaterDetailRows" :key="`rev_water_${idx}`" class="sub-row">
                            <td class="sticky-col" style="padding-left: 56px">- {{ item.text }}</td>
                            <td v-for="month in months" :key="`rev_water_${item.key}_${month.month}`" class="text-right">
                              {{ getDetailAmount(month, 'doanh_thu_tien_nuoc_detail', item.key, 'text') | formatCurrency }}
                            </td>
                            <td class="text-right total-col">{{ getDetailYearlyTotal('doanh_thu_tien_nuoc_detail', item.key, 'text') | formatCurrency }}</td>
                          </tr>
                        </template>

                        <!-- 1.4 Tiền rác (Expand) -->
                        <tr class="expandable-row sub-row" @click="toggleGeneric('showRevTrash')">
                          <td class="sticky-col pl-5">
                            Doanh thu tiền rác
                            <v-icon small class="ml-1">{{ showRevTrash ? 'expand_less' : 'expand_more' }}</v-icon>
                          </td>
                          <td v-for="month in months" :key="`rac_rev_${month.month}`" class="text-right">
                            {{ month.report_data.doanh_thu_tien_rac | formatCurrency }}
                          </td>
                          <td class="text-right total-col">{{ yearly_totals.doanh_thu_tien_rac | formatCurrency }}</td>
                        </tr>
                        <template v-if="showRevTrash">
                          <tr v-for="(item, idx) in revTrashDetailRows" :key="`rev_trash_${idx}`" class="sub-row">
                            <td class="sticky-col" style="padding-left: 56px">- {{ item.text }}</td>
                            <td v-for="month in months" :key="`rev_trash_${item.key}_${month.month}`" class="text-right">
                              {{ getDetailAmount(month, 'doanh_thu_tien_rac_detail', item.key, 'text') | formatCurrency }}
                            </td>
                            <td class="text-right total-col">{{ getDetailYearlyTotal('doanh_thu_tien_rac_detail', item.key, 'text') | formatCurrency }}</td>
                          </tr>
                        </template>

                        <!-- 1.5 Internet (Expand) -->
                        <tr class="expandable-row sub-row" @click="toggleGeneric('showRevInternet')">
                          <td class="sticky-col pl-5">
                            Doanh thu tiền internet
                            <v-icon small class="ml-1">{{ showRevInternet ? 'expand_less' : 'expand_more' }}</v-icon>
                          </td>
                          <td v-for="month in months" :key="`internet_rev_${month.month}`" class="text-right">
                            {{ month.report_data.doanh_thu_tien_internet | formatCurrency }}
                          </td>
                          <td class="text-right total-col">{{ yearly_totals.doanh_thu_tien_internet | formatCurrency }}</td>
                        </tr>
                        <template v-if="showRevInternet">
                          <tr v-for="(item, idx) in revInternetDetailRows" :key="`rev_internet_${idx}`" class="sub-row">
                            <td class="sticky-col" style="padding-left: 56px">- {{ item.text }}</td>
                            <td v-for="month in months" :key="`rev_internet_${item.key}_${month.month}`" class="text-right">
                               {{ getDetailAmount(month, 'doanh_thu_tien_internet_detail', item.key, 'text') | formatCurrency }}
                            </td>
                            <td class="text-right total-col">{{ getDetailYearlyTotal('doanh_thu_tien_internet_detail', item.key, 'text') | formatCurrency }}</td>
                          </tr>
                        </template>

                        <!-- 1.6 Xe máy (Expand) -->
                        <tr class="expandable-row sub-row" @click="toggleGeneric('showRevMotorbike')">
                          <td class="sticky-col pl-5">
                            Doanh thu tiền xe máy
                            <v-icon small class="ml-1">{{ showRevMotorbike ? 'expand_less' : 'expand_more' }}</v-icon>
                          </td>
                          <td v-for="month in months" :key="`xemay_rev_${month.month}`" class="text-right">
                            {{ month.report_data.doanh_thu_tien_xe_may | formatCurrency }}
                          </td>
                          <td class="text-right total-col">{{ yearly_totals.doanh_thu_tien_xe_may | formatCurrency }}</td>
                        </tr>
                        <template v-if="showRevMotorbike">
                          <tr v-for="(item, idx) in revMotorbikeDetailRows" :key="`rev_moto_${idx}`" class="sub-row">
                            <td class="sticky-col" style="padding-left: 56px">- {{ item.text }}</td>
                            <td v-for="month in months" :key="`rev_moto_${item.key}_${month.month}`" class="text-right">
                               {{ getDetailAmount(month, 'doanh_thu_tien_xe_may_detail', item.key, 'text') | formatCurrency }}
                            </td>
                            <td class="text-right total-col">{{ getDetailYearlyTotal('doanh_thu_tien_xe_may_detail', item.key, 'text') | formatCurrency }}</td>
                          </tr>
                        </template>

                        <!-- 1.7 Máy giặt (Expand) -->
                        <tr class="expandable-row sub-row" @click="toggleGeneric('showRevWashing')">
                          <td class="sticky-col pl-5">
                            Doanh thu tiền máy giặt
                            <v-icon small class="ml-1">{{ showRevWashing ? 'expand_less' : 'expand_more' }}</v-icon>
                          </td>
                          <td v-for="month in months" :key="`maygiat_rev_${month.month}`" class="text-right">
                            {{ month.report_data.doanh_thu_tien_may_giat | formatCurrency }}
                          </td>
                          <td class="text-right total-col">{{ yearly_totals.doanh_thu_tien_may_giat | formatCurrency }}</td>
                        </tr>
                        <template v-if="showRevWashing">
                          <tr v-for="(item, idx) in revWashingDetailRows" :key="`rev_wash_${idx}`" class="sub-row">
                            <td class="sticky-col" style="padding-left: 56px">- {{ item.text }}</td>
                            <td v-for="month in months" :key="`rev_wash_${item.key}_${month.month}`" class="text-right">
                               {{ getDetailAmount(month, 'doanh_thu_tien_may_giat_detail', item.key, 'text') | formatCurrency }}
                            </td>
                            <td class="text-right total-col">{{ getDetailYearlyTotal('doanh_thu_tien_may_giat_detail', item.key, 'text') | formatCurrency }}</td>
                          </tr>
                        </template>
                      </template>

                      <!-- 8. Điều chỉnh -->
                      <tr>
                        <td class="sticky-col pl-3">Điều chỉnh tiền phòng</td>
                        <td v-for="month in months" :key="`dieuchinh_${month.month}`" class="text-right" :class="month.report_data.dieu_chinh_tien_phong < 0 ? 'negative-value' : ''">
                          {{ formatNegativeValue(month.report_data.dieu_chinh_tien_phong) }}
                        </td>
                        <td class="text-right total-col" :class="yearly_totals.dieu_chinh_tien_phong < 0 ? 'negative-value' : ''">
                          {{ formatNegativeValue(yearly_totals.dieu_chinh_tien_phong) }}
                        </td>
                      </tr>

                      <!-- 9. Tiền cọc (Expand) -->
                      <tr class="expandable-row" @click="toggleGeneric('showRevDeposit')">
                        <td class="sticky-col pl-3">
                          Doanh thu tiền cọc
                          <v-icon small class="ml-1">{{ showRevDeposit ? 'expand_less' : 'expand_more' }}</v-icon>
                        </td>
                        <td v-for="month in months" :key="`coc_${month.month}`" class="text-right">
                          {{ month.report_data.doanh_thu_tien_coc | formatCurrency }}
                        </td>
                        <td class="text-right total-col">{{ yearly_totals.doanh_thu_tien_coc | formatCurrency }}</td>
                      </tr>
                      <template v-if="showRevDeposit">
                         <tr v-for="(item, idx) in revDepositDetailRows" :key="`rev_dep_${idx}`" class="sub-row">
                          <td class="sticky-col pl-5">- {{ item.text }}</td>
                          <td v-for="month in months" :key="`rev_dep_${item.key}_${month.month}`" class="text-right">
                             {{ getDetailAmount(month, 'doanh_thu_tien_coc_detail', item.key, 'text') | formatCurrency }}
                          </td>
                          <td class="text-right total-col">{{ getDetailYearlyTotal('doanh_thu_tien_coc_detail', item.key, 'text') | formatCurrency }}</td>
                        </tr>
                      </template>

                      <!-- 10. Chi trả cọc (Expand) -->
                      <tr class="expandable-row" @click="toggleGeneric('showRevReturnDeposit')">
                        <td class="sticky-col pl-3">
                          Chi trả cọc
                          <v-icon small class="ml-1">{{ showRevReturnDeposit ? 'expand_less' : 'expand_more' }}</v-icon>
                        </td>
                        <td v-for="month in months" :key="`tracoc_${month.month}`" class="text-right negative-value">
                          {{ formatNegativeValue(month.report_data.chi_tra_coc) }}
                        </td>
                        <td class="text-right total-col negative-value">{{ formatNegativeValue(yearly_totals.chi_tra_coc) }}</td>
                      </tr>
                      <template v-if="showRevReturnDeposit">
                        <tr v-for="(item, idx) in revReturnDepositDetailRows" :key="`rev_retdep_${idx}`" class="sub-row">
                          <td class="sticky-col pl-5">- {{ item.text }}</td>
                          <td v-for="month in months" :key="`rev_retdep_${item.key}_${month.month}`" class="text-right negative-value">
                             {{ formatExpenseValue(getDetailAmount(month, 'chi_tra_coc_detail', item.key, 'text')) }}
                          </td>
                          <td class="text-right total-col negative-value">{{ formatExpenseValue(getDetailYearlyTotal('chi_tra_coc_detail', item.key, 'text')) }}</td>
                        </tr>
                      </template>

                      <!-- 11. Thu khác -->
                      <tr>
                        <td class="sticky-col pl-3">Thu khác</td>
                        <td v-for="month in months" :key="`thukhac_${month.month}`" class="text-right">
                          {{ month.report_data.thu_khac | formatCurrency }}
                        </td>
                        <td class="text-right total-col">{{ yearly_totals.thu_khac | formatCurrency }}</td>
                      </tr>

                      <!-- CHI PHÍ -->
                      <tr class="section-header">
                        <td class="sticky-col"><strong>CHI PHÍ</strong></td>
                        <td v-for="month in months" :key="`chiphi_${month.month}`" class="text-right negative-value">
                          <strong>{{ formatExpenseValue(month.report_data.tong_chi_phi) }}</strong>
                        </td>
                        <td class="text-right total-col negative-value"><strong>{{ formatExpenseValue(yearly_totals.tong_chi_phi) }}</strong></td>
                      </tr>
                      
                      <!-- 1. Lương -->
                      <tr class="expandable-row" @click="toggleSalaryDetail">
                        <td class="sticky-col pl-3">
                          Chi phí lương và quản lý
                          <v-icon small class="ml-1">{{ showSalaryDetail ? 'expand_less' : 'expand_more' }}</v-icon>
                        </td>
                        <td v-for="month in months" :key="`luong_${month.month}`" class="text-right negative-value">
                          {{ formatExpenseValue(month.report_data.chi_phi_luong_va_quan_ly) }}
                        </td>
                        <td class="text-right total-col negative-value">{{ formatExpenseValue(yearly_totals.chi_phi_luong_va_quan_ly) }}</td>
                      </tr>
                      <template v-if="showSalaryDetail">
                       <template v-for="(item, idx) in salaryDetailRows">
                          <tr :key="`salary_detail_${idx}`" class="sub-row" 
                              :class="{ 'expandable-row': item.details && item.details.length > 0 }"
                              @click="item.details && item.details.length > 0 ? toggleSalarySubDetail(item.key) : null">
                            <td class="sticky-col pl-5">
                              - {{ item.text }}
                              <v-icon v-if="item.details && item.details.length > 0" small class="ml-1">{{ salarySubDetail[item.key] ? 'expand_less' : 'expand_more' }}</v-icon>
                            </td>
                            <td v-for="month in months" :key="`salary_${item.key}_${month.month}`" class="text-right negative-value">
                              {{ formatExpenseValue(getSalaryAmount(month, item.key)) }}
                            </td>
                            <td class="text-right total-col negative-value">{{ formatExpenseValue(getSalaryYearlyTotal(item.key)) }}</td>
                          </tr>
                          <!-- Sub-breakdown (Managers) -->
                          <template v-if="item.details && item.details.length > 0 && salarySubDetail[item.key]">
                              <tr v-for="(detail, dIdx) in item.details" :key="`salary_sub_${idx}_${dIdx}`" class="sub-row">
                                <td class="sticky-col" style="padding-left: 56px !important">• {{ detail.text }}</td>
                                <td v-for="month in months" :key="`salary_sub_${item.key}_${detail.text}_${month.month}`" class="text-right negative-value">
                                  {{ formatExpenseValue(getSalarySubDetailAmount(month, item.key, detail.text)) }}
                                </td>
                                <td class="text-right total-col negative-value">{{ formatExpenseValue(getSalarySubDetailYearlyTotal(item.key, detail.text)) }}</td>
                              </tr>
                          </template>
                        </template>
                      </template>
                      
                      <!-- 2. Thuê đất -->
                      <tr class="expandable-row" @click="toggleLandRentDetail">
                        <td class="sticky-col pl-3">
                          Chi phí thuê đất
                          <v-icon small class="ml-1">{{ showLandRentDetail ? 'expand_less' : 'expand_more' }}</v-icon>
                        </td>
                        <td v-for="month in months" :key="`thuedat_${month.month}`" class="text-right negative-value">
                          {{ formatExpenseValue(month.report_data.chi_phi_thue_dat) }}
                        </td>
                        <td class="text-right total-col negative-value">{{ formatExpenseValue(yearly_totals.chi_phi_thue_dat) }}</td>
                      </tr>
                      <template v-if="showLandRentDetail">
                        <tr v-for="(item, idx) in landRentDetailRows" :key="`land_detail_${idx}`" class="sub-row">
                          <td class="sticky-col pl-5">- {{ item.text }}</td>
                          <td v-for="month in months" :key="`land_${item.key}_${month.month}`" class="text-right negative-value">
                            {{ formatExpenseValue(getLandRentAmount(month, item.key)) }}
                          </td>
                          <td class="text-right total-col negative-value">{{ formatExpenseValue(getLandRentYearlyTotal(item.key)) }}</td>
                        </tr>
                      </template>

                      <!-- 3. Chi phí điện (Expand) -->
                      <tr class="expandable-row" @click="toggleGeneric('showExpElectric')">
                        <td class="sticky-col pl-3">
                          Chi phí điện
                          <v-icon small class="ml-1">{{ showExpElectric ? 'expand_less' : 'expand_more' }}</v-icon>
                        </td>
                        <td v-for="month in months" :key="`dien_${month.month}`" class="text-right negative-value">
                          {{ formatExpenseValue(month.report_data.chi_phi_dien) }}
                        </td>
                        <td class="text-right total-col negative-value">{{ formatExpenseValue(yearly_totals.chi_phi_dien) }}</td>
                      </tr>
                      <template v-if="showExpElectric">
                        <tr v-for="(item, idx) in expElectricDetailRows" :key="`exp_elec_${idx}`" class="sub-row">
                          <td class="sticky-col pl-5">- {{ item.text }}</td>
                          <td v-for="month in months" :key="`exp_elec_${item.key}_${month.month}`" class="text-right negative-value">
                            {{ formatExpenseValue(getDetailAmount(month, 'chi_phi_dien_detail', item.key, 'text')) }}
                          </td>
                          <td class="text-right total-col negative-value">{{ formatExpenseValue(getDetailYearlyTotal('chi_phi_dien_detail', item.key, 'text')) }}</td>
                        </tr>
                      </template>

                      <!-- 4. Chi phí nước (Expand) -->
                      <tr class="expandable-row" @click="toggleGeneric('showExpWater')">
                        <td class="sticky-col pl-3">
                          Chi phí nước
                          <v-icon small class="ml-1">{{ showExpWater ? 'expand_less' : 'expand_more' }}</v-icon>
                        </td>
                        <td v-for="month in months" :key="`nuoc_${month.month}`" class="text-right negative-value">
                          {{ formatExpenseValue(month.report_data.chi_phi_nuoc) }}
                        </td>
                        <td class="text-right total-col negative-value">{{ formatExpenseValue(yearly_totals.chi_phi_nuoc) }}</td>
                      </tr>
                      <template v-if="showExpWater">
                        <tr v-for="(item, idx) in expWaterDetailRows" :key="`exp_water_${idx}`" class="sub-row">
                          <td class="sticky-col pl-5">- {{ item.text }}</td>
                          <td v-for="month in months" :key="`exp_water_${item.key}_${month.month}`" class="text-right negative-value">
                            {{ formatExpenseValue(getDetailAmount(month, 'chi_phi_nuoc_detail', item.key, 'text')) }}
                          </td>
                          <td class="text-right total-col negative-value">{{ formatExpenseValue(getDetailYearlyTotal('chi_phi_nuoc_detail', item.key, 'text')) }}</td>
                        </tr>
                      </template>

                      <!-- 5. Rác -->
                      <tr>
                        <td class="sticky-col pl-3">Chi phí rác, vệ sinh</td>
                        <td v-for="month in months" :key="`rac_${month.month}`" class="text-right negative-value">
                          {{ formatExpenseValue(month.report_data.chi_phi_rac_ve_sinh) }}
                        </td>
                        <td class="text-right total-col negative-value">{{ formatExpenseValue(yearly_totals.chi_phi_rac_ve_sinh) }}</td>
                      </tr>
                      
                      <!-- 6. Thuế -->
                      <tr>
                        <td class="sticky-col pl-3">Chi phí thuế</td>
                        <td v-for="month in months" :key="`thue_${month.month}`" class="text-right negative-value">
                          {{ formatExpenseValue(month.report_data.chi_phi_thue) }}
                        </td>
                        <td class="text-right total-col negative-value">{{ formatExpenseValue(yearly_totals.chi_phi_thue) }}</td>
                      </tr>
                      
                      <!-- 7. Lãi vay (Expand) -->
                      <tr class="expandable-row" @click="toggleLoanInterestDetail">
                        <td class="sticky-col pl-3">
                          Chi phí lãi vay
                          <v-icon small class="ml-1">{{ showLoanInterestDetail ? 'expand_less' : 'expand_more' }}</v-icon>
                        </td>
                        <td v-for="month in months" :key="`laivay_${month.month}`" class="text-right negative-value">
                          {{ formatExpenseValue(month.report_data.chi_phi_lai_vay) }}
                        </td>
                        <td class="text-right total-col negative-value">{{ formatExpenseValue(yearly_totals.chi_phi_lai_vay) }}</td>
                      </tr>
                      <template v-if="showLoanInterestDetail">
                        <template v-for="(item, idx) in loanInterestDetailRows">
                          <tr :key="`loan_detail_${idx}`" class="sub-row">
                             <td class="sticky-col pl-5">- {{ item.text }}</td>
                             <td v-for="month in months" :key="`loan_${item.key}_${month.month}`" class="text-right negative-value">
                               {{ formatExpenseValue(getLoanCategoryAmount(month, item.key)) }}
                             </td>
                             <td class="text-right total-col negative-value">{{ formatExpenseValue(getLoanCategoryYearlyTotal(item.key)) }}</td>
                          </tr>
                          <!-- Sub-breakdown (Loan items) -->
                          <template v-if="item.details && item.details.length > 0">
                             <tr v-for="(detail, dIdx) in item.details" :key="`loan_sub_${idx}_${dIdx}`" class="sub-row">
                                <td class="sticky-col" style="padding-left: 56px !important">• {{ detail.text }}</td>
                                <td v-for="month in months" :key="`loan_sub_${item.key}_${detail.key}_${month.month}`" class="text-right negative-value">
                                  {{ formatExpenseValue(getLoanItemAmount(month, item.key, detail.key)) }}
                                </td>
                                <td class="text-right total-col negative-value">{{ formatExpenseValue(getLoanItemYearlyTotal(item.key, detail.key)) }}</td>
                             </tr>
                          </template>
                        </template>
                      </template>
                      
                      <!-- 8. Quảng cáo -->
                      <tr>
                        <td class="sticky-col pl-3">Chi phí quảng cáo</td>
                        <td v-for="month in months" :key="`quangcao_${month.month}`" class="text-right negative-value">
                          {{ formatExpenseValue(month.report_data.chi_phi_quang_cao) }}
                        </td>
                        <td class="text-right total-col negative-value">{{ formatExpenseValue(yearly_totals.chi_phi_quang_cao) }}</td>
                      </tr>
                      
                      <!-- 9. Internet -->
                      <tr>
                        <td class="sticky-col pl-3">Chi phí Internet</td>
                        <td v-for="month in months" :key="`internet_${month.month}`" class="text-right negative-value">
                          {{ formatExpenseValue(month.report_data.chi_phi_internet) }}
                        </td>
                        <td class="text-right total-col negative-value">{{ formatExpenseValue(yearly_totals.chi_phi_internet) }}</td>
                      </tr>
                      
                      <!-- 10. Vật tư (Expand) -->
                      <tr class="expandable-row" @click="toggleRepairDetail">
                        <td class="sticky-col pl-3">
                          Chi mua vật tư và sửa chữa
                          <v-icon small class="ml-1">{{ showRepairDetail ? 'expand_less' : 'expand_more' }}</v-icon>
                        </td>
                        <td v-for="month in months" :key="`vattu_${month.month}`" class="text-right negative-value">
                          {{ formatExpenseValue(month.report_data.chi_mua_vat_tu_va_sua_chua) }}
                        </td>
                        <td class="text-right total-col negative-value">{{ formatExpenseValue(yearly_totals.chi_mua_vat_tu_va_sua_chua) }}</td>
                      </tr>
                      <template v-if="showRepairDetail">
                         <tr v-for="(item, idx) in repairDetailRows" :key="`repair_detail_${idx}`" class="sub-row">
                          <td class="sticky-col pl-5">- {{ item.text }}</td>
                          <td v-for="month in months" :key="`rep_${item.key}_${month.month}`" class="text-right negative-value">
                            {{ formatExpenseValue(getRepairAmount(month, item.key)) }}
                          </td>
                          <td class="text-right total-col negative-value">{{ formatExpenseValue(getRepairYearlyTotal(item.key)) }}</td>
                        </tr>
                      </template>

                      <!-- 11. Dẫn phòng -->
                      <tr>
                        <td class="sticky-col pl-3">Chi phí dẫn phòng</td>
                        <td v-for="month in months" :key="`danphong_${month.month}`" class="text-right negative-value">
                          {{ formatExpenseValue(month.report_data.chi_phi_dan_phong) }}
                        </td>
                        <td class="text-right total-col negative-value">{{ formatExpenseValue(yearly_totals.chi_phi_dan_phong) }}</td>
                      </tr>
                      
                      <!-- 12. Khác -->
                      <tr>
                        <td class="sticky-col pl-3">Chi phí khác</td>
                        <td v-for="month in months" :key="`khac_${month.month}`" class="text-right negative-value">
                          {{ formatExpenseValue(month.report_data.chi_phi_khac) }}
                        </td>
                        <td class="text-right total-col negative-value">{{ formatExpenseValue(yearly_totals.chi_phi_khac) }}</td>
                      </tr>

                      <!-- LỢI NHUẬN -->
                      <tr class="profit-row">
                        <td class="sticky-col"><strong>LỢI NHUẬN</strong></td>
                        <td v-for="month in months" :key="`loinhuan_${month.month}`" class="text-right"
                            :class="month.report_data.loi_nhuan >= 0 ? 'positive-value' : 'negative-value'">
                          <strong>{{ formatNegativeValue(month.report_data.loi_nhuan) }}</strong>
                        </td>
                        <td class="text-right total-col" :class="yearly_totals.loi_nhuan >= 0 ? 'positive-value' : 'negative-value'">
                          <strong>{{ formatNegativeValue(yearly_totals.loi_nhuan) }}</strong>
                        </td>
                      </tr>
                      <tr class="profit-row">
                        <td class="sticky-col"><strong>%</strong></td>
                        <td v-for="month in months" :key="`tysuat_${month.month}`" class="text-right"
                            :class="month.report_data.ty_suat_loi_nhuan >= 0 ? 'positive-value' : 'negative-value'">
                          <strong>{{ month.report_data.ty_suat_loi_nhuan | formatPercentage }}</strong>
                        </td>
                        <td class="text-right total-col" :class="yearly_totals.ty_suat_loi_nhuan >= 0 ? 'positive-value' : 'negative-value'">
                          <strong>{{ yearly_totals.ty_suat_loi_nhuan | formatPercentage }}</strong>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                
                <!-- PHÂN TÍCH TỶ SUẤT LỢI NHUẬN -->
                <div v-if="yearly_totals.phan_tich_ty_suat_loi_nhuan" class="mt-5">
                  <h3 class="mb-3">PHÂN TÍCH TỶ SUẤT LỢI NHUẬN</h3>
                  <div class="table-responsive">
                    <table class="yearly-report-table margin-table">
                      <thead>
                        <tr>
                          <th class="text-left sticky-col" style="width: 40px">STT</th>
                          <th class="text-left sticky-col" style="min-width: 200px">Danh mục</th>
                          <th v-for="month in months" :key="month.month" class="text-right" style="min-width: 120px">{{ formatMonthDisplay(month.month) }}</th>
                          <th class="text-right sticky-col" style="min-width: 140px">Số dư cộng dồn</th>
                        </tr>
                      </thead>
                      <tbody>
                        <template v-for="(item, index) in profitMarginItems">
                          <tr :key="item.key + '_header'" class="margin-header-row">
                            <td rowspan="4" class="sticky-col">{{ index + 1 }}</td>
                            <td class="sticky-col"><strong>{{ item.label }}</strong></td>
                            <td v-for="month in months" :key="`rev_${item.key}_${month.month}`" class="text-right" :class="getMarginData(month, item.key).revenue < 0 ? 'negative-margin' : ''">
                              {{ formatNegativeValue(getMarginData(month, item.key).revenue) }}
                            </td>
                            <td class="text-right sticky-col" :class="getMarginDataYearly(item.key).revenue < 0 ? 'negative-margin' : ''">
                              {{ formatNegativeValue(getMarginDataYearly(item.key).revenue) }}
                            </td>
                          </tr>
                          <tr :key="item.key + '_expense'">
                            <td class="sticky-col pl-3">Chi phí</td>
                            <td v-for="month in months" :key="`exp_${item.key}_${month.month}`" class="text-right">
                              {{ formatExpenseValue(getMarginData(month, item.key).expense) }}
                            </td>
                            <td class="text-right total-col">
                               {{ formatExpenseValue(getMarginDataYearly(item.key).expense) }}
                            </td>
                          </tr>
                          <tr :key="item.key + '_profit'">
                            <td class="sticky-col pl-3"><strong>Net</strong></td>
                            <td v-for="month in months" :key="`prof_${item.key}_${month.month}`" class="text-right" :class="getMarginData(month, item.key).profit < 0 ? 'negative-margin' : 'positive-margin'">
                              <strong>{{ formatNegativeValue(getMarginData(month, item.key).profit) }}</strong>
                            </td>
                            <td class="text-right total-col" :class="getMarginDataYearly(item.key).profit < 0 ? 'negative-margin' : 'positive-margin'">
                               <strong>{{ formatNegativeValue(getMarginDataYearly(item.key).profit) }}</strong>
                            </td>
                          </tr>
                          <tr :key="item.key + '_margin'">
                            <td class="sticky-col pl-3"><strong>% Margin</strong></td>
                            <td v-for="month in months" :key="`marg_${item.key}_${month.month}`" class="text-right" :class="getMarginData(month, item.key).margin < 0 ? 'negative-margin' : 'positive-margin'">
                              <strong>{{ formatPercentageMethod(getMarginData(month, item.key).margin) }}</strong>
                            </td>
                            <td class="text-right total-col" :class="getMarginDataYearly(item.key).margin < 0 ? 'negative-margin' : 'positive-margin'">
                               <strong>{{ formatPercentageMethod(getMarginDataYearly(item.key).margin) }}</strong>
                            </td>
                          </tr>
                          <!-- Spacer row -->
                          <tr :key="item.key + '_spacer'" class="spacer-row" style="height: 20px;">
                            <td :colspan="months.length + 3"></td>
                          </tr>
                        </template>
                      </tbody>
                    </table>
                  </div>
                </div>
              </v-card-text>

              <!-- Error State -->
              <v-card-text v-else class="text-center pa-5">
                <v-icon size="60" color="grey lighten-1">error_outline</v-icon>
                <p class="mt-3 grey--text">Không có dữ liệu báo cáo</p>
              </v-card-text>
            </v-card>
          </div>
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
import api from "../../api";
import moment from "moment";
import swal from "sweetalert2";
import { ExcelExporter } from '../../helpers/ExcelExporter';

export default {
  filters: {
    formatCurrency(value) {
      if (value === null || value === undefined) return "-";
      return Number(value).toLocaleString("vi-VN");
    },
    formatPercentage(value) {
      if (value === null || value === undefined) return "-";
      return Number(value).toFixed(0) + "%";
    },
  },
  data() {
    return {
      loader: false,
      exporting: false,
      months: [],
      yearly_totals: {},
      chosen_year: moment().format("YYYY"),
      showSalaryDetail: false,
      salarySubDetail: {},
      showLandRentDetail: false,
      showLoanInterestDetail: false,
      showRepairDetail: false,
      // Revenue toggles
      showRevElectric: false,
      showRevWater: false,
      showRevTrash: false,
      showRevInternet: false,
      showRevMotorbike: false,
      showRevWashing: false,
      showRevDeposit: false,
      showRevReturnDeposit: false,
      showRoomRevenueDetail: false,
      showRoomBillDetail: false,
      // Expense toggles
      showExpElectric: false,
      showExpWater: false,
    };
  },
  watch: {
    chosen_year: function () {
      this.getData();
    },
  },
  computed: {
    customTitle() {
      return "Báo cáo kết quả kinh doanh cả năm";
    },
    yearOptions() {
      const currentYear = moment().year();
      const years = [];
      for (let y = 2025; y <= currentYear; y++) {
        years.push(String(y));
      }
      return years;
    },

    landRentDetailRows() {
      return this.aggregateDetailRows('chi_phi_thue_dat_detail');
    },
    profitMarginItems() {
      return [
        { key: 'tien_phong', label: 'Doanh thu tiền phòng' },
        { key: 'xe_may', label: 'Doanh thu tiền xe máy' },
        { key: 'dien', label: 'Doanh thu tiền điện' },
        { key: 'nuoc', label: 'Doanh thu tiền nước' },
        { key: 'rac', label: 'Doanh thu tiền rác' },
        { key: 'internet', label: 'Doanh thu tiền internet' },
      ];
    },
    // Revenue Details
    revRoomDetailRows() { return this.aggregateDetailRows('doanh_thu_tien_phong_detail', 'text', 'text'); },
    revElectricDetailRows() { return this.aggregateDetailRows('doanh_thu_tien_dien_detail', 'text', 'text'); },
    revWaterDetailRows() { return this.aggregateDetailRows('doanh_thu_tien_nuoc_detail', 'text', 'text'); },
    revTrashDetailRows() { return this.aggregateDetailRows('doanh_thu_tien_rac_detail', 'text', 'text'); },
    revInternetDetailRows() { return this.aggregateDetailRows('doanh_thu_tien_internet_detail', 'text', 'text'); },
    revMotorbikeDetailRows() { return this.aggregateDetailRows('doanh_thu_tien_xe_may_detail', 'text', 'text'); },
    revWashingDetailRows() { return this.aggregateDetailRows('doanh_thu_tien_may_giat_detail', 'text', 'text'); },
    revDepositDetailRows() { return this.aggregateDetailRows('doanh_thu_tien_coc_detail', 'text', 'text'); },
    revReturnDepositDetailRows() { return this.aggregateDetailRows('chi_tra_coc_detail', 'text', 'text'); },
    // Expense Details (others)
    expElectricDetailRows() { return this.aggregateDetailRows('chi_phi_dien_detail', 'text', 'text'); },
    expWaterDetailRows() { return this.aggregateDetailRows('chi_phi_nuoc_detail', 'text', 'text'); },
    loanInterestDetailRows() { 
      const uniqueItems = new Map();
      this.months.forEach(month => {
        const list = (month.report_data && month.report_data.chi_phi_lai_vay_detail) || [];
        list.forEach(item => {
          if (!uniqueItems.has(item.key)) {
            uniqueItems.set(item.key, { key: item.key, text: item.text, details: new Set() });
          }
          const existing = uniqueItems.get(item.key);
          if (item.details) {
            item.details.forEach(d => existing.details.add(d.text));
          }
        });
      });
      return Array.from(uniqueItems.values()).map(item => ({
        ...item,
        details: Array.from(item.details).map(text => ({ text }))
      }));
    },
    repairDetailRows() { return this.aggregateDetailRows('chi_mua_vat_tu_detail'); },

    salaryDetailRows() {
      const uniqueItems = new Map();
      this.months.forEach(month => {
        const list = (month.report_data && month.report_data.chi_phi_luong_detail) || [];
        list.forEach(item => {
          if (!uniqueItems.has(item.key)) {
            uniqueItems.set(item.key, { key: item.key, text: item.text, details: new Set() });
          }
          const existing = uniqueItems.get(item.key);
          if (item.details) {
            item.details.forEach(d => existing.details.add(d.text));
          }
        });
      });
      return Array.from(uniqueItems.values()).map(item => ({
        ...item,
        details: Array.from(item.details).map(text => ({ text }))
      }));
    },
  },
    mounted() {
    this.getData();
  },
  methods: {
    // Generic aggregator
    aggregateDetailRows(listName, keyField = 'key', textField = 'text') {
      const uniqueItems = new Map();
      this.months.forEach(month => {
        const list = (month.report_data && month.report_data[listName]) || [];
        list.forEach(item => {
          const k = item[keyField];
          if (!uniqueItems.has(k)) {
            uniqueItems.set(k, { key: k, text: item[textField] });
          }
        });
      });
      return Array.from(uniqueItems.values());
    },
    formatNegativeValue(value) {
      if (value === null || value === undefined) return '-';
      const num = Number(value);
      if (num === 0) return '0';
      if (num < 0) {
        return `(${Math.abs(num).toLocaleString('vi-VN')})`;
      }
      return num.toLocaleString('vi-VN');
    },
    formatExpenseValue(value) {
      if (value === null || value === undefined) return '-';
      const num = Number(value);
      if (num === 0) return '0';
      const formatted = Math.abs(num).toLocaleString('vi-VN');
      // Accounting style requested for Annual report: > 0 wrapped in parens (), < 0 no parens
      return num > 0 ? `(${formatted})` : formatted;
    },
    formatPercentageMethod(value) {
      if (value === null || value === undefined) return '-';
      const num = Number(value);
      if (num === 0) return '0%';
      if (num < 0) {
         return `(${Math.abs(num).toFixed(0)}%)`;
      }
      return Math.abs(num).toFixed(0) + "%";
    },
    getMarginData(month, type) {
       if (month.report_data && month.report_data.phan_tich_ty_suat_loi_nhuan) {
         return month.report_data.phan_tich_ty_suat_loi_nhuan[type] || { revenue: 0, expense: 0, profit: 0, margin: 0 };
       }
       return { revenue: 0, expense: 0, profit: 0, margin: 0 };
    },
    getMarginDataYearly(type) {
       if (this.yearly_totals && this.yearly_totals.phan_tich_ty_suat_loi_nhuan) {
         return this.yearly_totals.phan_tich_ty_suat_loi_nhuan[type] || { revenue: 0, expense: 0, profit: 0, margin: 0 };
       }
       return { revenue: 0, expense: 0, profit: 0, margin: 0 };
    },
    formatMonthDisplay(month) {
      return moment(month).format("MM/YYYY");
    },
    toggleSalaryDetail() {
      this.showSalaryDetail = !this.showSalaryDetail;
    },
    toggleSalarySubDetail(key) {
      this.$set(this.salarySubDetail, key, !this.salarySubDetail[key]);
    },
    toggleLandRentDetail() {
      this.showLandRentDetail = !this.showLandRentDetail;
    },
    toggleGeneric(field) {
       this[field] = !this[field];
    },
    toggleLoanInterestDetail() {
      this.showLoanInterestDetail = !this.showLoanInterestDetail;
    },
    toggleRepairDetail() {
       this.showRepairDetail = !this.showRepairDetail;
    },
    // Generic amount getter
    getDetailAmount(month, listName, key, keyField = 'key') {
      const list = month.report_data && month.report_data[listName];
      if (!list) return 0;
      const item = list.find(d => d[keyField] === key);
      return item ? Math.abs(item.amount) : 0;
    },
    getAmountFromList(list, key) {
      if (!list) return 0;
      const item = list.find(d => d.key === key);
      return item ? Math.abs(item.amount) : 0;
    },
    getDetailYearlyTotal(listName, key, keyField = 'key') {
       return this.months.reduce((sum, month) => sum + this.getDetailAmount(month, listName, key, keyField), 0);
    },
    // Specific Getters calling generic one
    getSalaryAmount(month, key) {
       return this.getDetailAmount(month, 'chi_phi_luong_detail', key);
    },
    getSalaryYearlyTotal(key) {
      return this.getDetailYearlyTotal('chi_phi_luong_detail', key);
    },
    getSalarySubDetailAmount(month, parentKey, subName) {
       const list = month.report_data && month.report_data.chi_phi_luong_detail;
       if (!list) return 0;
       const parent = list.find(d => d.key === parentKey);
       if (!parent || !parent.details) return 0;
       const sub = parent.details.find(d => d.text === subName);
       return sub ? Math.abs(sub.amount) : 0;
    },
    getSalarySubDetailYearlyTotal(parentKey, subName) {
       return this.months.reduce((sum, month) => sum + this.getSalarySubDetailAmount(month, parentKey, subName), 0);
    },
    getLandRentAmount(month, key) {
      return this.getDetailAmount(month, 'chi_phi_thue_dat_detail', key);
    },
    getLandRentYearlyTotal(key) {
      return this.getDetailYearlyTotal('chi_phi_thue_dat_detail', key);
    },
    getRepairAmount(month, key) {
      return this.getAmountFromList(month.report_data && month.report_data.chi_mua_vat_tu_detail, key);
    },
    getRepairYearlyTotal(key) {
      return this.months.reduce((sum, month) => sum + this.getRepairAmount(month, key), 0);
    },
    getLoanCategoryAmount(month, key) {
       return this.getAmountFromList(month.report_data && month.report_data.chi_phi_lai_vay_detail, key);
    },
    getLoanCategoryYearlyTotal(key) {
      return this.months.reduce((sum, month) => sum + this.getLoanCategoryAmount(month, key), 0);
    },
    // For getting amount of a specific loan object within a category
    getLoanItemAmount(month, categoryKey, itemKey) {
      const list = month.report_data && month.report_data.chi_phi_lai_vay_detail;
      if (!list) return 0;
      const category = list.find(d => d.key === categoryKey);
      if (!category || !category.details) return 0;
      
      // Note: In backend, details have 'key', but in consistent schema we might want to track by index or key.
      // Key is displayed text. 'details' schema: { key, amount }
      const item = category.details.find(d => d.key === itemKey);
      return item ? Math.abs(item.amount) : 0;
    },
    getLoanItemYearlyTotal(categoryKey, itemKey) {
      return this.months.reduce((sum, month) => sum + this.getLoanItemAmount(month, categoryKey, itemKey), 0);
    },
    async getData() {
      this.loader = true;
      this.months = [];
      this.yearly_totals = {};

      try {
        const result = await api.getYearlyBusinessReport(`year=${this.chosen_year}`);

        if (result.status === 200) {
          this.months = result.data.months || [];
          this.yearly_totals = result.data.yearly_totals || {};
        } else {
          swal.fire("Lỗi", result.message || "Không thể tải báo cáo", "error");
        }
      } catch (error) {
        console.error("Error loading report:", error);
        swal.fire("Lỗi", "Không thể tải báo cáo", "error");
      }

      this.loader = false;
    },
    async exportExcel() {
        if (!this.months || this.months.length === 0) return;
        this.exporting = true;
        try {
            // 1. Define Columns
            const columns = [
                { header: 'Chỉ tiêu', key: 'name', width: 35, style: { bold: true } }
            ];
            
            // Dynamic Month Columns
            this.months.forEach(m => {
                columns.push({
                    header: this.formatMonthDisplay(m.month),
                    key: m.month,
                    width: 15,
                    align: 'right',
                    format: 'currency'
                });
            });
            
            // Total Column
            columns.push({ header: 'Số dư cộng dồn', key: 'total', width: 20, align: 'right', format: 'currency', style: { bold: true } });

            const data = [];

            // Helper to create a row
            // getValue: (month) => number
            // getTotal: () => number
            const createRow = (name, getValue, getTotal, style = {}, outlineLevel = undefined) => {
                const row = { name, style };
                if (style && style.indent) row.outlineLevel = style.indent;
                if (!isNaN(outlineLevel)) row.outlineLevel = outlineLevel;
                if (this.months) {
                    this.months.forEach(monthItem => {
                        const val = getValue(monthItem);
                        row[monthItem.month] = val;
                    });
                }
                if (getTotal) {
                    row['total'] = getTotal();
                }
                data.push(row);
            };

            // Helper for Simple Field extraction
            // fieldPath: string 'report_data.field'
            const createFieldRow = (name, fieldName, style = {}) => {
                createRow(
                    name,
                    (m) => m.report_data ? m.report_data[fieldName] : 0,
                    () => this.yearly_totals ? this.yearly_totals[fieldName] : 0,
                    style
                );
            };

            // --- REVENUE ---
            data.push({ name: 'DOANH THU', style: { bold: true, background: 'FFE3F2FD' } });
            
            // Total Revenue row (just for info, but the table has detailed breakdowns first)
            // The table starts with 'Doanh thu tiền phòng' breakdown, then others.
            // Actually the table has a TOTAL REVENUE Header row with values.
            createFieldRow('TỔNG DOANH THU', 'tong_doanh_thu', { bold: true, background: 'FFFFF3E0' });

            // 1. Room Revenue (Generic)
            createFieldRow('Doanh thu tiền phòng', 'tong_doanh_thu_tien_phong');
            
            // 1.1 Room Price
            createFieldRow('Doanh thu tiền phòng', 'doanh_thu_tien_phong', { indent: 1 });
            // Details
            this.revRoomDetailRows.forEach(item => {
                createRow(
                    item.text,
                    (m) => this.getDetailAmount(m, 'doanh_thu_tien_phong_detail', item.key, 'text'),
                    () => this.getDetailYearlyTotal('doanh_thu_tien_phong_detail', item.key, 'text'),
                    { indent: 2 }
                );
            });

            // 1.2 Electric
            createFieldRow('Doanh thu tiền điện', 'doanh_thu_tien_dien', { indent: 1 });
            this.revElectricDetailRows.forEach(item => {
                createRow(item.text, (m) => this.getDetailAmount(m, 'doanh_thu_tien_dien_detail', item.key, 'text'), () => this.getDetailYearlyTotal('doanh_thu_tien_dien_detail', item.key, 'text'), { indent: 2 });
            });
            // 1.3 Water
            createFieldRow('Doanh thu tiền nước', 'doanh_thu_tien_nuoc', { indent: 1 });
            this.revWaterDetailRows.forEach(item => {
                 createRow(item.text, (m) => this.getDetailAmount(m, 'doanh_thu_tien_nuoc_detail', item.key, 'text'), () => this.getDetailYearlyTotal('doanh_thu_tien_nuoc_detail', item.key, 'text'), { indent: 2 });
            });
            // 1.4 Trash
            createFieldRow('Doanh thu tiền rác', 'doanh_thu_tien_rac', { indent: 1 });
            this.revTrashDetailRows.forEach(item => {
                 createRow(item.text, (m) => this.getDetailAmount(m, 'doanh_thu_tien_rac_detail', item.key, 'text'), () => this.getDetailYearlyTotal('doanh_thu_tien_rac_detail', item.key, 'text'), { indent: 2 });
            });
            // 1.5 Internet
            createFieldRow('Doanh thu tiền internet', 'doanh_thu_tien_internet', { indent: 1 });
            this.revInternetDetailRows.forEach(item => {
                 createRow(item.text, (m) => this.getDetailAmount(m, 'doanh_thu_tien_internet_detail', item.key, 'text'), () => this.getDetailYearlyTotal('doanh_thu_tien_internet_detail', item.key, 'text'), { indent: 2 });
            });
            // 1.6 Motorbike
            createFieldRow('Doanh thu tiền xe máy', 'doanh_thu_tien_xe_may', { indent: 1 });
            this.revMotorbikeDetailRows.forEach(item => {
                 createRow(item.text, (m) => this.getDetailAmount(m, 'doanh_thu_tien_xe_may_detail', item.key, 'text'), () => this.getDetailYearlyTotal('doanh_thu_tien_xe_may_detail', item.key, 'text'), { indent: 2 });
            });
            // 1.7 Washing
            createFieldRow('Doanh thu tiền máy giặt', 'doanh_thu_tien_may_giat', { indent: 1 });
            this.revWashingDetailRows.forEach(item => {
                 createRow(item.text, (m) => this.getDetailAmount(m, 'doanh_thu_tien_may_giat_detail', item.key, 'text'), () => this.getDetailYearlyTotal('doanh_thu_tien_may_giat_detail', item.key, 'text'), { indent: 2 });
            });

            // Adjust
            createFieldRow('Điều chỉnh tiền phòng', 'dieu_chinh_tien_phong');

            // Deposit
            createFieldRow('Doanh thu tiền cọc', 'doanh_thu_tien_coc');
            this.revDepositDetailRows.forEach(item => {
                 createRow(item.text, (m) => this.getDetailAmount(m, 'doanh_thu_tien_coc_detail', item.key, 'text'), () => this.getDetailYearlyTotal('doanh_thu_tien_coc_detail', item.key, 'text'), { indent: 2 });
            });

            // Return Deposit
            // THESE ARE EXPENSES LOGICALLY BUT IN REVENUE SECTION.
            // In table, they are formatted as Negative if > 0 (using formatNegativeValue for display?).
            // Actually, `chi_tra_coc` is formatted with `formatNegativeValue` which encloses negative numbers in parens.
            // If `chi_tra_coc` is negative, it shows (X).
            // Usually `chi_tra_coc` is a negative flow.
            createFieldRow('Chi trả cọc', 'chi_tra_coc');
            this.revReturnDepositDetailRows.forEach(item => {
                 createRow(item.text, (m) => this.getDetailAmount(m, 'chi_tra_coc_detail', item.key, 'text'), () => this.getDetailYearlyTotal('chi_tra_coc_detail', item.key, 'text'), { indent: 1 });
            });

            createFieldRow('Thu khác', 'thu_khac');


            // --- EXPENSES ---
            data.push({ name: 'CHI PHÍ', style: { bold: true, background: 'FFE3F2FD' } });
            
            // Helper for Expense (Negate values for Display: 100 -> -100)
            const createExpenseRow = (name, fieldName, style = {}) => {
                createRow(
                    name,
                    (m) => { const v = m.report_data ? m.report_data[fieldName] : 0; return v ? -v : 0; },
                    () => { const v = this.yearly_totals ? this.yearly_totals[fieldName] : 0; return v ? -v : 0; },
                    style
                );
            };

            createExpenseRow('TỔNG CHI PHÍ', 'tong_chi_phi', { bold: true, background: 'FFFFF3E0' });

            // 1. Salary
            createExpenseRow('Chi phí lương và quản lý', 'chi_phi_luong_va_quan_ly');
            this.salaryDetailRows.forEach(item => {
                // Main Salary Item
                 createRow(
                    item.text,
                    (m) => { const v = this.getSalaryAmount(m, item.key); return v ? -v : 0; },
                    () => { const v = this.getSalaryYearlyTotal(item.key); return v ? -v : 0; },
                    { indent: 1 }
                );
                // Sub details
                if (item.details) {
                    item.details.forEach(sub => {
                        createRow(
                            sub.text,
                            (m) => { const v = this.getSalarySubDetailAmount(m, item.key, sub.text); return v ? -v : 0; },
                            () => { const v = this.getSalarySubDetailYearlyTotal(item.key, sub.text); return v ? -v : 0; },
                            { indent: 2 }
                        );
                    });
                }
            });

            // 2. Land Rent
            createExpenseRow('Chi phí thuê đất', 'chi_phi_thue_dat');
            this.landRentDetailRows.forEach(item => {
                 createRow(
                    item.text,
                    (m) => { const v = this.getLandRentAmount(m, item.key); return v ? -v : 0; },
                    () => { const v = this.getLandRentYearlyTotal(item.key); return v ? -v : 0; },
                    { indent: 1 }
                );
            });

            // 3. Electric Exp
            createExpenseRow('Chi phí điện', 'chi_phi_dien');
            this.expElectricDetailRows.forEach(item => {
                createRow(item.text, (m) => { const v = this.getDetailAmount(m, 'chi_phi_dien_detail', item.key, 'text'); return v ? -v : 0; }, () => { const v = this.getDetailYearlyTotal('chi_phi_dien_detail', item.key, 'text'); return v ? -v : 0; }, { indent: 1 });
            });

            // 4. Water Exp
            createExpenseRow('Chi phí nước', 'chi_phi_nuoc');
            this.expWaterDetailRows.forEach(item => {
                 createRow(item.text, (m) => { const v = this.getDetailAmount(m, 'chi_phi_nuoc_detail', item.key, 'text'); return v ? -v : 0; }, () => { const v = this.getDetailYearlyTotal('chi_phi_nuoc_detail', item.key, 'text'); return v ? -v : 0; }, { indent: 1 });
            });

            createExpenseRow('Chi phí rác, vệ sinh', 'chi_phi_rac_ve_sinh');
            createExpenseRow('Chi phí thuế', 'chi_phi_thue');

            // 7. Loan Interest
            createExpenseRow('Chi phí lãi vay', 'chi_phi_lai_vay');
            this.loanInterestDetailRows.forEach(item => {
                 // Category
                 if (item.text !== 'Khác') {
                     createRow(
                        item.text,
                         (m) => { const v = this.getLoanCategoryAmount(m, item.key); return v ? -v : 0; },
                         () => { const v = this.getLoanCategoryYearlyTotal(item.key); return v ? -v : 0; },
                         { indent: 1 }
                     );
                 }
                 if (item.details) {
                     item.details.forEach(sub => {
                         if (sub.text === 'Khác') return;
                         createRow(
                             sub.text,
                             (m) => { const v = this.getLoanItemAmount(m, item.key, sub.text); return v ? -v : 0; },
                             () => { const v = this.getLoanItemYearlyTotal(item.key, sub.text); return v ? -v : 0; },
                             { indent: 2 }
                         );
                     });
                 }
            });

            createExpenseRow('Chi phí quảng cáo', 'chi_phi_quang_cao');
            createExpenseRow('Chi phí Internet', 'chi_phi_internet');

            // 10. Repair
            createExpenseRow('Chi mua vật tư và sửa chữa', 'chi_mua_vat_tu_va_sua_chua');
            this.repairDetailRows.forEach(item => {
                createRow(item.text, (m) => { const v = this.getRepairAmount(m, item.key); return v ? -v : 0; }, () => { const v = this.getRepairYearlyTotal(item.key); return v ? -v : 0; }, { indent: 1 });
            });

            createExpenseRow('Chi phí dẫn phòng', 'chi_phi_dan_phong');
            createExpenseRow('Chi phí khác', 'chi_phi_khac');

            // --- PROFIT ---
            data.push({ name: 'LỢI NHUẬN', style: { bold: true, background: 'FFE8F5E9' } });
            createFieldRow('Lợi nhuận', 'loi_nhuan', { bold: true });

            // Margin %
            // Need custom row because it's %, not currency
            const marginRow = { name: '%', style: { bold: true } };
            this.months.forEach(m => {
                 const val = m.report_data ? m.report_data.ty_suat_loi_nhuan : 0;
                 marginRow[m.month] = val ? val + '%' : '0%';
            });
            const totalMargin = this.yearly_totals ? this.yearly_totals.ty_suat_loi_nhuan : 0;
            marginRow['total'] = totalMargin ? totalMargin + '%' : '0%';
            data.push(marginRow);


             // --- ANALYSIS SECTION ---
             data.push({ name: '', total: '' });
             data.push({ name: 'PHÂN TÍCH TỶ SUẤT LỢI NHUẬN', style: { bold: true, fontSize: 13 } });
             
             // Analysis Items
             this.profitMarginItems.forEach((item, idx) => {
                 data.push({ name: `${idx + 1}. ${item.label}`, style: { bold: true, background: 'FFE8F5E9' } });
                 
                 // Revenue
                 createRow(
                     'Doanh thu',
                     (m) => this.getMarginData(m, item.key).revenue,
                     () => this.getMarginDataYearly(item.key).revenue,
                     { indent: 1 },
                     0
                 );
                 // Expense
                 createRow(
                     'Chi phí',
                     (m) => { const v = this.getMarginData(m, item.key).expense; return v ? -v : 0; },
                     () => { const v = this.getMarginDataYearly(item.key).expense; return v ? -v : 0; },
                     { indent: 1 },
                     0
                 );
                 // Net
                 createRow(
                     'Net',
                     (m) => this.getMarginData(m, item.key).profit,
                     () => this.getMarginDataYearly(item.key).profit,
                     { indent: 1, bold: true },
                     0
                 );
                 // Margin
                 const mRow = { name: '% Margin', style: { indent: 1, italic: true }, outlineLevel: 0 };
                 this.months.forEach(m => mRow[m.month] = this.formatPercentageMethod(this.getMarginData(m, item.key).margin));
                 mRow['total'] = this.formatPercentageMethod(this.getMarginDataYearly(item.key).margin);
                 data.push(mRow);
                 
                 data.push({ name: '', total: '' }); // spacer
             });

            await ExcelExporter.exportReport({
                title: 'BÁO CÁO KẾT QUẢ KINH DOANH',
                subtitle: `Năm ${this.chosen_year}`,
                columns: columns,
                data: data,
                fileName: `BaoCaoKQKD_${this.chosen_year}`
            });

        } catch (error) {
            console.error(error);
            swal.fire("Lỗi", "Không thể xuất file Excel", "error");
        }
        this.exporting = false;
    },
  },
};
</script>

<style scoped>
.table-responsive {
  overflow-x: auto;
}

.yearly-report-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 1200px;
}

.yearly-report-table th,
.yearly-report-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #e0e0e0;
  white-space: nowrap;
}

.yearly-report-table th {
  background-color: #f5f5f5;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 1;
}

.yearly-report-table .sticky-col {
  position: sticky;
  left: 0;
  background-color: #fff;
  z-index: 2;
}

.yearly-report-table th.sticky-col {
  z-index: 3;
  background-color: #f5f5f5;
}

.yearly-report-table .section-header {
  background-color: #e3f2fd;
}

.yearly-report-table .section-header .sticky-col {
  background-color: #e3f2fd;
}

.yearly-report-table .profit-row {
  background-color: #e8f5e9;
}

.yearly-report-table .profit-row .sticky-col {
  background-color: #e8f5e9;
}

.yearly-report-table .sub-row {
  background-color: #fafafa;
}

.yearly-report-table .sub-row .sticky-col {
  background-color: #fafafa;
}

.yearly-report-table .expandable-row {
  cursor: pointer;
}

.yearly-report-table .expandable-row:hover {
  background-color: #f0f0f0;
}

.yearly-report-table .margin-header-row td {
  background-color: #f5f5f5;
  font-weight: bold;
}

.spacer-row td {
  border: none !important;
  background-color: transparent !important;
}


.yearly-report-table .total-col {
  /* background-color: #fff8e1; REMOVED */
  background-color: transparent !important;
  font-weight: 600;
}

.pl-3 {
  padding-left: 24px !important;
}

.pl-5 {
  padding-left: 40px !important;
}

.negative-value {
  /* color: #d32f2f; REMOVED to keep only parentheses without red color */
}

.positive-value {
  color: #388e3c;
}

.negative-margin {
  color: #d32f2f;
}

.positive-margin {
  color: #388e3c;
}
</style>
